import mapboxgl from 'mapbox-gl';
import EventEmitter from 'events';
import data from './data.json';
import './style.css';

function create(map, options = {}) {
  const {
    selected: initial = [],
    sourceUrl = 'mapbox://tslessar.8b05vmdc',
    sourceLayer = 'Localsearch_Statistical_Areas-5gx09k',
    sourceIdentifier = 'id',
    tooltipHTML = ({ name }) => name,
  } = options;
  let tooltipTimeout;

  const tooltipPopup = new mapboxgl.Popup({
    className: 'statistical-area-plugin-tooltip',
    offset: 5,
    closeButton: false,
    closeOnClick: false,
    closeOnMove: true,
  });

  const selected = [...initial];

  const events = new EventEmitter();

  const on = (...args) => events.on(...args);

  const off = (...args) => events.off(...args);

  const draw = () => map.setFilter('Selected', ['in', sourceIdentifier, ...selected]);

  const getSelected = () => [...selected];

  const selectedExists = (key) => selected.includes(key);

  const addSelected = (...keys) => {
    keys.forEach((key) => {
      if (!selectedExists(key)) {
        selected.push(key);
      }
    });
    draw();
  };

  const removeSelected = (...keys) => {
    keys.forEach((key) => {
      if (selectedExists(key)) {
        selected.splice(selected.indexOf(key), 1);
      }
    });
    draw();
  };

  const toggleSelected = (...keys) => {
    keys.forEach((key) => {
      if (!selectedExists(key)) {
        selected.push(key);
      } else {
        selected.splice(selected.indexOf(key), 1);
      }
    });
    draw();
  };

  const clearSelected = () => {
    selected.splice(0, selected.length);
    draw();
  };

  const matchFns = {
    equal: (compare, compareTo) => compare === compareTo,
    prefix: (compare, compareTo) => compare.startsWith(compareTo),
  };

  const queryAll = (ids = [], opts = {}) => {
    let { match } = opts;
    if (typeof match !== 'function') {
      match = matchFns[match] || matchFns.equal;
    }
    return data.filter((item) => ids.find((id) => match(item.id, id)));
  };

  const query = (id) => {
    const [result] = queryAll([id]);
    return result;
  };

  const addLayers = () => {
    const sourceName = 'statisticalAreas';

    map.addSource(sourceName, {
      type: 'vector',
      url: sourceUrl,
    });

    map.addLayer({
      id: 'select',
      type: 'fill',
      source: sourceName,
      'source-layer': sourceLayer,
      paint: {
        'fill-color': 'transparent',
      },
    });
  };

  const onClick = (e) => {
    const [feature] = map.queryRenderedFeatures(e.point, {
      layers: ['select'],
    });

    if (feature) {
      const { properties } = feature;
      events.emit('click', properties[sourceIdentifier], properties);
    }
  };

  const cancelTooltip = () => {
    clearTimeout(tooltipTimeout);
    tooltipPopup.remove();
  };

  const showTooltip = (e) => {
    cancelTooltip();

    const { lngLat, features = [] } = e;
    const [feature] = features;
    if (!feature) {
      return;
    }

    tooltipTimeout = setTimeout(() => {
      tooltipPopup.setLngLat(lngLat).setHTML(tooltipHTML(feature.properties)).addTo(map);
    }, 500);
  };

  const onMouseOut = () => {
    map.getCanvas().style.cursor = ''; // eslint-disable-line no-param-reassign
    cancelTooltip();
  };

  const onMouseMove = (e) => {
    showTooltip(e);
  };

  const onMouseEnter = () => { map.getCanvas().style.cursor = 'pointer'; }; // eslint-disable-line no-param-reassign

  const onMouseLeave = () => { map.getCanvas().style.cursor = ''; }; // eslint-disable-line no-param-reassign

  const onLoad = () => {
    map.setLayoutProperty('Selected', 'visibility', 'visible');
    addLayers();
    draw();
  };

  const dispose = () => {
    map.off('load', onLoad);
    map.off('click', onClick);
    map.off('mouseout', onMouseOut);
    map.off('mousemove', 'select', onMouseMove);
    map.off('mouseenter', 'select', onMouseEnter);
    map.off('mouseleave', 'select', onMouseLeave);
    map.off('remove', dispose);
  };

  const init = () => {
    map.on('load', onLoad);
    map.on('click', onClick);
    map.on('mouseout', onMouseOut);
    map.on('mousemove', 'select', onMouseMove);
    map.on('mouseenter', 'select', onMouseEnter);
    map.on('mouseleave', 'select', onMouseLeave);
    map.on('remove', dispose);
  };

  init();

  return {
    on,
    off,
    selectedExists,
    addSelected,
    removeSelected,
    toggleSelected,
    clearSelected,
    getSelected,
    query,
    queryAll,
  };
}

const statisticalAreaPlugin = {
  create,
};

export default statisticalAreaPlugin;
